import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Settings, X, Calendar, CreditCard, Crown, Users, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const SubscriptionManager = ({ onClose, subscriptionInfo, ninjaProfiles }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // Datos de la suscripción
  const planName = subscriptionInfo?.displayName || 'Plan Free';
  const billingCycle = subscriptionInfo?.billingCycle || 'monthly';
  const cycleText = billingCycle === 'annual' ? 'Anual' : 'Mensual';
  const priceMxn = parseFloat(subscriptionInfo?.priceMxn) || 0;
  const expiresAt = subscriptionInfo?.expiresAt;
  const status = subscriptionInfo?.status || 'active';
  const ninjaLimit = subscriptionInfo?.ninjaLimit || 0;
  const ninjaUsed = ninjaProfiles?.length || 0;
  
  // Formatear fecha
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-MX', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const handleManageBilling = () => {
    // TODO: Integrar con PayPal Customer Portal
    alert('🚀 Portal de facturación próximamente.\n\n✅ Por ahora, contacta a soporte@numerologiacotidiana.com para gestionar tu facturación.');
  };

  const handleUpgradePlan = () => {
    onClose();
    navigate('/pricing');
  };

  const handleBuyNinjas = () => {
    onClose();
    // El botón de compra de ninjas en AppPage se encarga de esto
    alert('💡 Usa el botón "🥷 Comprar Perfiles Adicionales" en la parte superior para agregar más perfiles Ninja.');
  };

  // Detectar si tiene bonus 2026
  const paidAt = subscriptionInfo?.paidAt;
  const isOraculo365 = planName?.toLowerCase().includes('oráculo');
  const isHorizonte60 = planName?.toLowerCase().includes('horizonte');
  const isAnnual = billingCycle === 'annual';
  const currentYear = new Date().getFullYear();
  const contractYear = paidAt ? new Date(paidAt).getFullYear() : currentYear;
  const hasBonus2026 = contractYear === 2025 && isAnnual && (isOraculo365 || isHorizonte60);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="bg-morado text-white p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Settings className="w-7 h-7" />
              <div>
                <h2 className="text-2xl font-bold">Mi Suscripción</h2>
                <p className="text-sm opacity-90">
                  Plan {planName} • {cycleText}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          
          {/* Estado de la Suscripción */}
          <div className="bg-morado/10 rounded-xl p-5 border border-morado/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-morado flex items-center gap-2">
                <Crown className="w-5 h-5 text-[#d4af37]" />
                Estado de la Cuenta
              </h3>
              <div className={`px-3 py-1 rounded-full text-sm font-semibold ${
                status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              }`}>
                {status === 'active' ? '✅ Activo' : '❌ Inactivo'}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-gray-600 mb-1">Plan</div>
                <div className="font-semibold text-morado flex items-center gap-2">
                  {planName}
                  {isOraculo365 && <Sparkles className="w-4 h-4 text-[#d4af37]" />}
                </div>
              </div>
              <div>
                <div className="text-gray-600 mb-1">Ciclo de Pago</div>
                <div className="font-semibold">{cycleText}</div>
              </div>
              <div>
                <div className="text-gray-600 mb-1">Precio</div>
                <div className="font-semibold text-morado">${priceMxn.toFixed(2)} MXN</div>
              </div>
              <div>
                <div className="text-gray-600 mb-1">Próxima Renovación</div>
                <div className="font-semibold">{formatDate(expiresAt)}</div>
              </div>
            </div>
          </div>

          {/* Perfiles Ninja */}
          <div className="bg-morado/5 rounded-xl p-5 border border-morado/20">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-morado flex items-center gap-2">
                <Users className="w-5 h-5" />
                Perfiles Ninja
              </h3>
              <div className="text-right">
                <div className="text-2xl font-bold text-morado">
                  {ninjaUsed} / {ninjaLimit}
                </div>
                <div className="text-xs text-gray-600">usados / total</div>
              </div>
            </div>
            
            <p className="text-sm text-gray-600 mb-3">
              Los perfiles Ninja te permiten consultar la información numerológica de otras personas (familia, pareja, hijos, clientes).
            </p>

            {/* Desglose de límites */}
            {(subscriptionInfo?.ninjaLimitPurchased > 0 || subscriptionInfo?.ninjaLimitManual > 0) && (
              <div className="bg-white rounded-lg p-3 border border-morado/20 mb-3 space-y-2 text-sm">
                <div className="font-semibold text-morado mb-2">📊 Desglose de tus perfiles:</div>
                <div className="flex justify-between">
                  <span className="text-gray-600">• Incluidos en tu plan:</span>
                  <span className="font-semibold">{subscriptionInfo?.ninjaLimitBase || 0}</span>
                </div>
                {subscriptionInfo?.ninjaLimitPurchased > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">• Perfiles comprados:</span>
                    <span className="font-semibold text-green-600">+{subscriptionInfo.ninjaLimitPurchased}</span>
                  </div>
                )}
                {subscriptionInfo?.ninjaLimitManual > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">• Agregados manualmente:</span>
                    <span className="font-semibold text-blue-600">+{subscriptionInfo.ninjaLimitManual}</span>
                  </div>
                )}
                <div className="pt-2 mt-2 border-t border-morado/20 flex justify-between">
                  <span className="font-semibold text-gray-700">Total disponibles:</span>
                  <span className="font-bold text-morado">{ninjaLimit}</span>
                </div>
              </div>
            )}

            {/* Información de renovación de perfiles Ninja comprados */}
            {subscriptionInfo?.ninjaLimitPurchased > 0 && (
              <div className="bg-green-50 rounded-lg p-4 border-2 border-green-200 mb-3">
                <div className="flex items-start gap-3">
                  <CreditCard className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="font-semibold text-green-800 mb-2">
                      💳 Renovación Automática Mensual
                    </div>
                    <div className="text-sm text-gray-700 space-y-1.5">
                      <div className="flex justify-between items-center">
                        <span>Perfiles Ninja adicionales:</span>
                        <span className="font-semibold">{subscriptionInfo.ninjaLimitPurchased} × $79 MXN</span>
                      </div>
                      <div className="flex justify-between items-center pt-1.5 border-t border-green-200">
                        <span className="font-semibold">Cargo mensual:</span>
                        <span className="font-bold text-green-700 text-lg">
                          ${(subscriptionInfo.ninjaLimitPurchased * 79).toFixed(2)} MXN/mes
                        </span>
                      </div>
                      <div className="mt-2 pt-2 border-t border-green-200 text-xs text-gray-600">
                        <Calendar className="w-3.5 h-3.5 inline mr-1" />
                        Se renueva automáticamente cada mes de forma independiente a tu plan {planName}.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {ninjaUsed < ninjaLimit ? (
              <div className="bg-white rounded-lg p-3 border border-morado/20">
                <p className="text-sm text-morado">
                  ✨ Tienes <strong>{ninjaLimit - ninjaUsed} perfil{ninjaLimit - ninjaUsed !== 1 ? 'es' : ''} disponible{ninjaLimit - ninjaUsed !== 1 ? 's' : ''}</strong>.
                </p>
              </div>
            ) : (
              <div className="bg-white rounded-lg p-3 border border-gray-200">
                <p className="text-sm text-gray-600 mb-2">
                  Has usado todos tus perfiles Ninja disponibles.
                </p>
                <button
                  onClick={handleBuyNinjas}
                  className="text-sm text-morado font-semibold hover:underline"
                >
                  💎 Comprar perfiles adicionales ($79 MXN/mes c/u) →
                </button>
              </div>
            )}
          </div>

          {/* Beneficios Especiales */}
          {(isOraculo365 || hasBonus2026) && (
            <div className="bg-amber-50 rounded-xl p-5 border-2 border-[#d4af37]">
              <h3 className="text-lg font-semibold text-morado flex items-center gap-2 mb-3">
                <Sparkles className="w-5 h-5 text-[#d4af37]" />
                🎁 Beneficios Exclusivos
              </h3>
              
              <div className="space-y-2 text-sm">
                {isOraculo365 && (
                  <div className="bg-white rounded-lg p-3 border border-amber-200">
                    <p className="font-semibold text-morado mb-1">🎁 Sorpresas Adicionales Cada Mes</p>
                    <p className="text-gray-600">
                      Como miembro Oráculo 365, recibes contenido exclusivo, herramientas especiales y sorpresas numerológicas mensuales.
                    </p>
                  </div>
                )}
                
                {hasBonus2026 && (
                  <div className="bg-white rounded-lg p-3 border border-amber-200">
                    <p className="font-semibold text-[#d97706] mb-1">🎉 Regalo Especial 2025</p>
                    <p className="text-gray-600">
                      Por contratar tu plan anual en 2025, tienes acceso hasta el <strong>31 de diciembre de 2026</strong> completamente gratis.
                      {isOraculo365 && ' ¡Disfruta de 14 meses de visión numerológica!'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Detalles del Plan */}
          <div className="bg-gray-50 rounded-xl p-5">
            <h3 className="font-semibold text-morado mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Información del Plan
            </h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Fecha de contratación:</span>
                <span className="font-semibold">{formatDate(paidAt)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Válido hasta:</span>
                <span className="font-semibold">{formatDate(expiresAt)}</span>
              </div>
              {hasBonus2026 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Acceso extendido hasta:</span>
                  <span className="font-semibold text-[#d97706]">31 de diciembre de 2026 🎁</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-600">Perfiles Ninja incluidos:</span>
                <span className="font-semibold">{ninjaLimit}</span>
              </div>
            </div>
          </div>

          {/* Nota sobre renovación */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <p className="text-sm text-blue-800">
              <strong>ℹ️ Renovación:</strong> Recibirás un recordatorio por email 5 días antes de que venza tu plan. Podrás renovar fácilmente desde la página de precios.
            </p>
          </div>

          {/* Acciones */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition font-semibold"
            >
              Cerrar
            </button>
            <button
              onClick={handleUpgradePlan}
              className="flex-1 px-6 py-3 bg-morado text-white rounded-xl hover:shadow-lg transition font-semibold flex items-center justify-center gap-2"
            >
              <Crown className="w-5 h-5" />
              Ver Otros Planes
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default SubscriptionManager;
